// Packages
import {
  createContext,
  ReactNode,
  useState,
  useReducer,
  useContext,
  useEffect,
} from "react";

// Components
import {
  clearAuthData,
  getStartAuthDetails,
  setAuthData,
  getAuthToken,
  setAuthToken,
} from "../utils/auth";
import {
  checkAccessTokenApi,
  checkRefreshTokenApi,
  logoutApi,
  refreshTokenApi,
} from "../services/auth";
import { AxiosError } from "axios";
import { isToken } from "typescript";

type AuthActions = "LOGIN" | "LOGOUT" | "CLEAR";
export type UserRole = "user" | "admin" | "accManager" | null;

export type AuthResult = {
  role: UserRole;
  userId: string | null;
};

type State = AuthResult & {
  isAuthenticated: boolean;
};

type Action = {
  type: AuthActions;
  payload?: State;
};

const Default_Auth_State: State = {
  role: null,
  userId: null,
  isAuthenticated: false,
};

const startState = getStartAuthDetails() ?? Default_Auth_State;

type Dispatch = (action: Action) => void;

const AuthContext = createContext({} as State);
const StateContext = createContext<State | undefined>({} as State);
const DispatchContext = createContext<Dispatch | undefined>(undefined);

function AuthReducer(state: State, action: Action): State {
  switch (action.type) {
    case "LOGIN":
      return { ...state, ...action.payload, isAuthenticated: true };
    case "LOGOUT":
      return { ...Default_Auth_State, ...action.payload };
    case "CLEAR":
      return { ...Default_Auth_State };
    default:
      return state;
  }
}

function updateAuthState(
  dispatch: Dispatch,
  context: State,
  authData: AuthResult,
  isAuthenticated: boolean
) {
  if (isAuthenticated) {
    dispatch({
      type: "LOGIN",
      payload: {
        ...context,
        ...authData,
      },
    });
    setAuthData(authData);
  } else {
    dispatch({
      type: "CLEAR",
    });
    clearAuthData();
  }
}

function clearAuthState(dispatch: Dispatch) {
  dispatch({
    type: "CLEAR",
  });
  clearAuthData();
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {

  const [state, dispatch] = useReducer(AuthReducer, startState);
  const [isTokenValidated, setIsTokenValidated] = useState<Boolean | null>(null);

  const logoutUser = async() => {
    const logoutResult = await logoutApi();

    if (logoutResult) {
      updateAuthState(dispatch, state, Default_Auth_State, false)
    }
  }

  useEffect(() => {
  const authorize = async() => {
    try {
      const token = getAuthToken();
      if (token) {
        await checkAccessTokenApi();
        try {
          await checkRefreshTokenApi();
        } catch (e) {
          logoutUser();
          setIsTokenValidated(false)
        }
        setIsTokenValidated(true)
      } else {
        logoutUser();
        setIsTokenValidated(false)
      }
    } catch (e) {
      if (e instanceof AxiosError && e.response && e.response.status === 403) {
        try {
          await checkRefreshTokenApi();
          const refreshTokenResult = await refreshTokenApi(null);
          if (refreshTokenResult) {
            setIsTokenValidated(true)
            setAuthToken(refreshTokenResult.accessToken);
          }
        } catch (ee) {
          if (ee instanceof AxiosError && ee.response && ee.response.status == 404) {
            logoutUser()
            setIsTokenValidated(false)
          } else if (ee instanceof AxiosError && ee.response && ee.response.status == 403) {
            logoutUser()
            setIsTokenValidated(false)
          }
        }
      } else if (e instanceof AxiosError && e.response && e.response.status === 401) {
        logoutUser();
        setIsTokenValidated(false)
      } else {
        logoutUser();
        setIsTokenValidated(false)
      }
    }
  }
  authorize()
}, [])

if (isTokenValidated === null) {
  return null;
}

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }

  return {
    ...context,
  };
};

export const useUpdateAuth = () => {
  const context = useContext(StateContext);
  const dispatch = useContext(DispatchContext);

  if (!context || !dispatch) {
    throw new Error("useUpdateAuth must be used inside an AuthProvider");
  }

  return (result: AuthResult, isAuthenticated: boolean) => {
    updateAuthState(dispatch, context, result, isAuthenticated);
  };
};

export const useClearAuth = () => {
  const dispatch = useContext(DispatchContext);
  if (!dispatch) {
    throw new Error("useClearAuth must be used inside an AuthProvider");
  }

  return () => {
    clearAuthState(dispatch);
  };
};

export const refreshToken = async () => {
  const refreshTokenResult = await refreshTokenApi(null);
  setAuthToken(refreshTokenResult.accessToken);
};

export default AuthContext;
